<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center gap-3 mb-5">
        <a href="/products"
          class="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800
                 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition shrink-0">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
          </svg>
        </a>
        <div class="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center
                    text-indigo-600 dark:text-indigo-400 font-bold text-base shrink-0">
          {{ product.name.charAt(0).toUpperCase() }}
        </div>
        <div class="flex-1 min-w-0">
          <h1 class="text-xl font-bold text-slate-800 dark:text-white truncate">{{ product.name }}</h1>
          <p class="text-slate-400 text-sm">Product History · purchases, sales &amp; profit</p>
        </div>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <a href="/products"
            class="inline-flex items-center gap-1.5 text-white/80 text-xs font-semibold mb-3">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
            </svg>
            Products
          </a>
          <h1 class="text-xl font-bold text-white mb-0.5">{{ product.name }}</h1>
          <p class="text-indigo-200 text-xs mb-3">Product History</p>

          <div v-if="!loading" class="space-y-2">
            <div class="grid grid-cols-3 gap-2">
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-indigo-200 uppercase tracking-widest mb-1">In Stock</p>
                <p class="text-sm font-bold text-white">{{ stats.in_stock }}</p>
              </div>
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-blue-200 uppercase tracking-widest mb-1">Purchased</p>
                <p class="text-sm font-bold text-white">{{ stats.total_purchased_qty }}</p>
              </div>
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Sold</p>
                <p class="text-sm font-bold text-white">{{ stats.total_sold_qty }}</p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-sky-200 uppercase tracking-widest mb-1">Buy Amt</p>
                <p class="text-xs font-bold text-white whitespace-nowrap">Rs {{ shortNum(stats.total_purchased_amount) }}</p>
              </div>
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-yellow-200 uppercase tracking-widest mb-1">Profit</p>
                <p class="text-xs font-bold text-white whitespace-nowrap">Rs {{ shortNum(stats.total_profit) }}</p>
              </div>
              <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
                <p class="text-[9px] font-bold text-violet-200 uppercase tracking-widest mb-1">Margin</p>
                <p class="text-sm font-bold text-white">{{ stats.margin_percent }}%</p>
              </div>
            </div>
          </div>
          <div v-else class="space-y-2">
            <div class="grid grid-cols-3 gap-2">
              <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
            </div>
            <div class="grid grid-cols-3 gap-2">
              <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
            </div>
          </div>
        </div>

        <!-- Filter card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <div class="flex gap-2">
            <button v-for="f in filters" :key="f.value" @click="activeFilter = f.value"
              class="flex-1 py-2 rounded-xl text-sm font-semibold transition"
              :class="activeFilter === f.value
                ? 'bg-indigo-500 text-white shadow-sm'
                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              {{ f.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- ── DESKTOP STATS ── -->
      <template v-if="loading">
        <div class="hidden md:grid grid-cols-5 gap-3 mb-4">
          <div v-for="i in 5" :key="i" class="h-20 bg-slate-100 dark:bg-slate-800 rounded-2xl animate-pulse"></div>
        </div>
      </template>
      <template v-else>
        <div class="hidden md:grid grid-cols-5 gap-3 mb-4">
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">In Stock</p>
            <p class="text-2xl font-bold"
              :class="stats.in_stock > 0
                ? 'text-slate-800 dark:text-white'
                : stats.in_stock === 0
                  ? 'text-slate-400'
                  : 'text-red-500'">
              {{ stats.in_stock }}
            </p>
            <p class="text-xs text-slate-400 mt-0.5">Opening: {{ stats.opening_qty }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-blue-400 uppercase tracking-wide mb-1">Purchased</p>
            <p class="text-2xl font-bold text-slate-800 dark:text-white">{{ stats.total_purchased_qty }}</p>
            <p class="text-xs text-slate-400 mt-0.5">Rs {{ formatNum(stats.total_purchased_amount) }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide mb-1">Sold</p>
            <p class="text-2xl font-bold text-slate-800 dark:text-white">{{ stats.total_sold_qty }}</p>
            <p class="text-xs text-slate-400 mt-0.5">Rs {{ formatNum(stats.total_sold_amount) }}</p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-amber-500 uppercase tracking-wide mb-1">Total Profit</p>
            <p class="text-2xl font-bold"
              :class="stats.total_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
              Rs {{ shortNum(stats.total_profit) }}
            </p>
          </div>
          <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
            <p class="text-[10px] font-semibold text-violet-500 uppercase tracking-wide mb-1">Margin %</p>
            <p class="text-2xl font-bold"
              :class="stats.margin_percent >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
              {{ stats.margin_percent }}%
            </p>
          </div>
        </div>
      </template>

      <!-- ── DESKTOP FILTER ── -->
      <div class="hidden md:flex items-center gap-2 mb-3">
        <button v-for="f in filters" :key="f.value" @click="activeFilter = f.value"
          class="px-4 py-2 rounded-xl text-sm font-semibold transition"
          :class="activeFilter === f.value
            ? 'bg-indigo-500 text-white shadow-sm shadow-indigo-500/30'
            : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'">
          {{ f.label }}
          <span v-if="activeFilter === f.value" class="ml-1.5 text-[10px] opacity-75">{{ meta.total }}</span>
        </button>
      </div>

      <!-- ── LOADING SKELETON ── -->
      <template v-if="loading">
        <!-- Desktop -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-6">
            <div v-for="i in 8" :key="i" class="h-2.5 w-14 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 7" :key="i" class="flex items-center gap-4 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-5 w-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
            <div class="h-3 w-28 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse flex-1"></div>
            <div class="h-3 w-10 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
        </div>
        <!-- Mobile -->
        <div class="md:hidden space-y-3">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-3">
            <div class="flex items-center justify-between">
              <div class="h-5 w-16 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
              <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="grid grid-cols-3 gap-2">
              <div v-for="j in 3" :key="j" class="h-12 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY STATE ── -->
      <div v-else-if="meta.total === 0"
        class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ activeFilter === 'all' ? 'No transactions yet' : `No ${activeFilter} transactions` }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ activeFilter === 'all'
            ? 'Transactions will appear here once invoices include this product'
            : 'Try switching the filter above' }}
        </p>
        <button v-if="activeFilter !== 'all'" @click="activeFilter = 'all'"
          class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">
          Show all transactions
        </button>
      </div>

      <!-- ── DESKTOP TABLE ── -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-10">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Invoice</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Type</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Party</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Qty</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Rate</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Amount</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Profit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(tx, i) in transactions" :key="tx.id"
              class="border-t border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
              <td class="px-5 py-4 text-slate-400 text-xs">{{ pageOffset + i + 1 }}</td>
              <td class="px-5 py-4 text-slate-500 dark:text-slate-400 whitespace-nowrap">{{ tx.date }}</td>
              <td class="px-5 py-4">
                <a :href="`/invoices/${tx.invoice_id}/edit`"
                  class="text-indigo-500 hover:text-indigo-400 font-medium">#{{ tx.invoice_number }}</a>
              </td>
              <td class="px-5 py-4">
                <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold"
                  :class="tx.type === 'purchased'
                    ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                    : 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'">
                  {{ tx.type === 'purchased' ? 'Purchase' : 'Sale' }}
                </span>
              </td>
              <td class="px-5 py-4">
                <p class="font-medium text-slate-800 dark:text-white">{{ tx.customer_name }}</p>
                <p class="text-xs text-slate-400 mt-0.5">{{ tx.customer_shop }}</p>
              </td>
              <td class="px-5 py-4 text-right font-medium text-slate-700 dark:text-slate-300">{{ tx.quantity }}</td>
              <td class="px-5 py-4 text-right">
                <span class="font-medium text-slate-700 dark:text-slate-300">Rs {{ formatNum(tx.rate) }}</span>
                <span v-if="tx.type === 'sold'" class="block text-xs text-slate-400 mt-0.5">Cost: Rs {{ formatNum(tx.purchase_rate) }}</span>
              </td>
              <td class="px-5 py-4 text-right font-semibold"
                :class="tx.type === 'purchased'
                  ? 'text-slate-700 dark:text-slate-300'
                  : 'text-emerald-600 dark:text-emerald-400'">
                Rs {{ formatNum(tx.amount) }}
              </td>
              <td class="px-5 py-4 text-right">
                <span v-if="tx.profit !== null" class="font-semibold"
                  :class="tx.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
                  Rs {{ formatNum(tx.profit) }}
                </span>
                <span v-else class="text-slate-300 dark:text-slate-600">—</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <td colspan="5" class="px-5 py-3.5">
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {{ pageOffset + 1 }}–{{ Math.min(pageOffset + meta.per_page, meta.total) }}
                  of {{ meta.total }} transaction{{ meta.total !== 1 ? 's' : '' }}
                </span>
              </td>
              <td class="px-5 py-3.5 text-right">
                <span class="text-sm font-bold text-slate-700 dark:text-slate-300">{{ pageTotals.qty }}</span>
              </td>
              <td></td>
              <td class="px-5 py-3.5 text-right">
                <span class="text-sm font-bold text-slate-800 dark:text-white">Rs {{ formatNum(pageTotals.amount) }}</span>
              </td>
              <td class="px-5 py-3.5 text-right">
                <span class="text-sm font-bold"
                  :class="pageTotals.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
                  Rs {{ formatNum(pageTotals.profit) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── DESKTOP PAGINATION ── -->
      <div v-if="!loading && meta.total > 0"
        class="hidden md:flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
        <!-- Per page -->
        <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span>Show</span>
          <div class="relative">
            <select v-model="perPage" @change="onPerPageChange"
              class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                     text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-1.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
              <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
            </select>
            <svg class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>
          <span>of {{ meta.total }} records</span>
        </div>
        <!-- Page nav -->
        <div v-if="meta.pages > 1" class="flex items-center gap-1">
          <button @click="goToPage(meta.current_page - 1)" :disabled="meta.current_page <= 1"
            class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                   disabled:opacity-40 disabled:cursor-not-allowed transition">
            ‹
          </button>
          <template v-for="pg in pageNumbers" :key="pg">
            <span v-if="pg === '...'" class="px-2 text-slate-400">…</span>
            <button v-else @click="goToPage(pg)"
              class="w-9 h-9 rounded-xl text-sm border transition"
              :class="pg === meta.current_page
                ? 'bg-indigo-500 text-white border-indigo-500'
                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
              {{ pg }}
            </button>
          </template>
          <button @click="goToPage(meta.current_page + 1)" :disabled="meta.current_page >= meta.pages"
            class="px-3 py-1.5 rounded-xl text-sm border border-slate-200 dark:border-slate-700
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800
                   disabled:opacity-40 disabled:cursor-not-allowed transition">
            ›
          </button>
        </div>
      </div>

      <!-- ── MOBILE CARDS ── -->
      <div v-if="!loading && meta.total > 0" class="md:hidden space-y-3">
        <div v-for="tx in transactions" :key="tx.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
          <!-- Header row -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold"
                :class="tx.type === 'purchased'
                  ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                  : 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'">
                {{ tx.type === 'purchased' ? 'Purchase' : 'Sale' }}
              </span>
              <span class="text-xs text-slate-400">{{ tx.date }}</span>
            </div>
            <a :href="`/invoices/${tx.invoice_id}/edit`"
              class="text-indigo-500 text-xs font-semibold">#{{ tx.invoice_number }}</a>
          </div>

          <!-- Party -->
          <div class="flex items-center gap-2.5 mb-3">
            <div class="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center
                        text-slate-500 dark:text-slate-400 text-xs font-bold shrink-0">
              {{ tx.customer_name.charAt(0).toUpperCase() }}
            </div>
            <div class="min-w-0">
              <p class="text-sm font-medium text-slate-800 dark:text-white truncate">{{ tx.customer_name }}</p>
              <p class="text-xs text-slate-400 truncate">{{ tx.customer_shop }}</p>
            </div>
          </div>

          <!-- Stats grid -->
          <div class="grid gap-2" :class="tx.type === 'sold' ? 'grid-cols-3' : 'grid-cols-2'">
            <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2.5 text-center">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Qty</p>
              <p class="text-sm font-bold text-slate-700 dark:text-slate-300">{{ tx.quantity }}</p>
            </div>
            <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2.5 text-center">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Rate</p>
              <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Rs {{ formatNum(tx.rate) }}</p>
              <p v-if="tx.type === 'sold'" class="text-[10px] text-slate-400">Cost: {{ formatNum(tx.purchase_rate) }}</p>
            </div>
            <div v-if="tx.type === 'sold'" class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2.5 text-center">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Profit</p>
              <p class="text-sm font-bold"
                :class="tx.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
                Rs {{ formatNum(tx.profit) }}
              </p>
            </div>
          </div>

          <!-- Amount row -->
          <div class="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <span class="text-xs text-slate-400">{{ tx.quantity }} × Rs {{ formatNum(tx.rate) }}</span>
            <span class="font-bold text-sm"
              :class="tx.type === 'purchased' ? 'text-slate-700 dark:text-slate-300' : 'text-emerald-600 dark:text-emerald-400'">
              Rs {{ formatNum(tx.amount) }}
            </span>
          </div>
        </div>
      </div>

      <!-- ── MOBILE LOAD MORE ── -->
      <div v-if="!loading && meta.total > 0" class="md:hidden mt-4 pb-6 flex flex-col items-center gap-3">
        <button v-if="hasMoreMobile" @click="loadMore" :disabled="loadingMore"
          class="flex items-center gap-2 px-6 py-2.5 rounded-2xl text-sm font-semibold border border-slate-200 dark:border-slate-700
                 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300
                 disabled:opacity-50 active:scale-95 transition w-full justify-center">
          <svg v-if="loadingMore" class="w-4 h-4 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
          </svg>
          <span>{{ loadingMore ? 'Loading…' : 'Load more' }}</span>
        </button>
        <p v-else class="text-xs text-slate-400">All caught up ✓</p>
      </div>

    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  product: { type: Object, required: true }
})

const inertiaPage  = usePage()
const businessName = computed(() => inertiaPage.props.auth?.user?.business_name?.trim() || 'Master Ledger')

// ── State ───────────────────────────────────────────────────────
const loading      = ref(true)
const loadingMore  = ref(false)
const transactions = ref([])
const stats = ref({
  opening_qty: 0, total_purchased_qty: 0, total_purchased_amount: 0,
  total_sold_qty: 0, total_sold_amount: 0, in_stock: 0,
  total_profit: 0, margin_percent: 0,
})
const meta = ref({ total: 0, pages: 1, current_page: 1, per_page: 10 })

// ── Filter ──────────────────────────────────────────────────────
const filters = [
  { value: 'all',       label: 'All' },
  { value: 'purchased', label: 'Purchases' },
  { value: 'sold',      label: 'Sales' },
]
const activeFilter = ref('all')

// ── Pagination ──────────────────────────────────────────────────
const perPageOptions = [10, 20, 50, 100, 250]
const perPage        = ref(10)

const pageOffset = computed(() => (meta.value.current_page - 1) * meta.value.per_page)

const pageNumbers = computed(() => {
  const total   = meta.value.pages
  const current = meta.value.current_page
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (current <= 4)          return [1, 2, 3, 4, 5, '...', total]
  if (current >= total - 3)  return [1, '...', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '...', current - 1, current, current + 1, '...', total]
})

// ── Fetch ───────────────────────────────────────────────────────
async function fetchData(append = false) {
  if (append) loadingMore.value = true
  else        loading.value     = true

  try {
    const params = new URLSearchParams({
      page:     meta.value.current_page,
      per_page: perPage.value,
    })
    if (activeFilter.value !== 'all') params.set('type', activeFilter.value)

    const res  = await fetch(`/products/${props.product.id}/history/data?${params}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    const json = await res.json()

    stats.value = json.stats || stats.value
    meta.value  = json.meta  || meta.value

    if (append) transactions.value.push(...(json.transactions || []))
    else        transactions.value = json.transactions || []
  } finally {
    loading.value     = false
    loadingMore.value = false
  }
}

onMounted(() => {
  document.title = `${props.product.name} History | ${businessName.value}`
  fetchData()
})

// ── Actions ─────────────────────────────────────────────────────
function goToPage(p) {
  if (p < 1 || p > meta.value.pages) return
  meta.value.current_page = p
  fetchData()
}

function onPerPageChange() {
  meta.value.current_page = 1
  fetchData()
}

function loadMore() {
  meta.value.current_page++
  fetchData(true)
}

watch(activeFilter, () => {
  meta.value.current_page = 1
  fetchData()
})

// ── Mobile helpers ──────────────────────────────────────────────
const hasMoreMobile = computed(() => meta.value.current_page < meta.value.pages)

// ── Page totals (current page only) ────────────────────────────
const pageTotals = computed(() => ({
  qty:    transactions.value.reduce((s, t) => s + t.quantity, 0),
  amount: transactions.value.reduce((s, t) => s + t.amount, 0),
  profit: transactions.value.filter(t => t.profit !== null).reduce((s, t) => s + t.profit, 0),
}))

// ── Utilities ───────────────────────────────────────────────────
function formatNum(n) {
  return Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function shortNum(n) {
  const abs  = Math.abs(Number(n))
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000) return sign + (abs / 1_000_000).toFixed(1) + 'M'
  if (abs >= 1_000)     return sign + Math.floor(abs / 1_000) + 'K'
  return sign + Math.floor(abs).toLocaleString('en-PK')
}
</script>
